import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMe, useLogin, useLogout, useSignup, setAuthTokenGetter, getGetMeQueryKey } from "@workspace/api-client-react";
import type { LoginInput, SignupInput, User } from "@workspace/api-client-react";

setAuthTokenGetter(() => localStorage.getItem("ayrshire_token"));

const USER_CACHE_KEY = "ayrshire_user_cache";

function readCachedUser(): User | undefined {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as User;
  } catch {
    return undefined;
  }
}

function writeCachedUser(u: User | null | undefined) {
  try {
    if (u) sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else sessionStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore storage errors
  }
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (data: LoginInput) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  signup: (data: SignupInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(localStorage.getItem("ayrshire_token"));

  const cachedUser = readCachedUser();

  const { data: user, isLoading: isUserLoading } = useGetMe({
    query: {
      queryKey: getGetMeQueryKey(),
      enabled: !!token,
      retry: false,
      // Use cached session user as placeholder so pages render immediately on refresh.
      // The query still verifies with the server in the background; if the token is
      // expired the placeholder is discarded and pages redirect to login.
      placeholderData: cachedUser,
    }
  });

  // Keep session cache in sync with resolved user data
  useEffect(() => {
    if (user) writeCachedUser(user);
  }, [user]);

  // Heartbeat: tell the server this web user is active
  useEffect(() => {
    if (!token) return;
    const ping = () => {
      fetch("/api/ping", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "x-signup-source": "web" },
      }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 3 * 60 * 1000);
    return () => clearInterval(id);
  }, [token]);

  const loginMutation = useLogin();
  const signupMutation = useSignup();
  const logoutMutation = useLogout();

  const handleLogin = async (data: LoginInput) => {
    const result = await loginMutation.mutateAsync({ data });
    localStorage.setItem("ayrshire_token", result.token);
    writeCachedUser(result.user);
    queryClient.setQueryData(getGetMeQueryKey(), result.user);
    setToken(result.token);
  };

  const handleSignup = async (data: SignupInput) => {
    const result = await signupMutation.mutateAsync({ data });
    localStorage.setItem("ayrshire_token", result.token);
    writeCachedUser(result.user);
    queryClient.setQueryData(getGetMeQueryKey(), result.user);
    setToken(result.token);
  };

  const handleLoginWithToken = async (incomingToken: string) => {
    localStorage.setItem("ayrshire_token", incomingToken);
    setToken(incomingToken);
    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${incomingToken}` },
      });
      if (res.ok) {
        const userData = await res.json() as User;
        queryClient.setQueryData(getGetMeQueryKey(), userData);
      }
    } catch {
      // Best-effort — useGetMe will re-fetch on next render with token enabled
    }
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      void fetch("/api/admin/office-phones/media-session", { method: "DELETE", credentials: "same-origin", keepalive: true }).catch(() => {});
      localStorage.removeItem("ayrshire_token");
      writeCachedUser(null);
      setToken(null);
      queryClient.removeQueries({ queryKey: getGetMeQueryKey() });
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isLoading: isUserLoading,
        login: handleLogin,
        loginWithToken: handleLoginWithToken,
        signup: handleSignup,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
